import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ImageOff } from 'lucide-react';
import { Album } from '../albumList/album';
import { API_BASE, asArray } from '../../../config/api';
import { coverArtUrl } from '../../../config/images';
import { InlineCandidateList, PickerItem } from './InlineCandidateList';
import { GenreChips, ArtistOrder } from './FieldEditors';
import {
    ErrorToast,
    ToastError,
    toastErrorFromException,
    toastErrorFromResponse,
} from './ErrorToast';
import './EditModal.css';

interface Props {
    album: Album;
    onClose: () => void;
    onSaved: (album: Album) => void;
}

// AlbumCandidate as sent by GET /api/album?id=&candidates=matches.
interface AlbumMatchCandidate {
    mbid: string;
    title: string;
    first_release_date?: string;
    primary_type?: string;
    secondary_types?: string[];
}

// coverart.ImageCandidate as sent by GET /api/album?id=&candidates=art.
interface AlbumArtCandidate {
    url?: string;
    source: 'coverartarchive' | 'embedded';
}

type Tab = 'match' | 'art' | 'refresh';

function matchCandidateSublabel(c: AlbumMatchCandidate): string {
    const year = c.first_release_date ? c.first_release_date.slice(0, 4) : null;
    const type = [c.primary_type, ...(c.secondary_types ?? [])].filter(Boolean).join(', ');
    return [year, type].filter(Boolean).join(' · ') || 'Unknown release';
}

function artCandidateLabel(c: AlbumArtCandidate): string {
    return c.source === 'embedded' ? 'Embedded picture' : 'Cover Art Archive';
}

export function AlbumEditModal({ album, onClose, onSaved }: Props): React.JSX.Element {
    const [title, setTitle] = useState(album.getTitle());
    const [releaseDate, setReleaseDate] = useState(album.getReleaseDate());
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const [activeTab, setActiveTab] = useState<Tab | null>(null);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerError, setPickerError] = useState<string | null>(null);
    // Apply failures go here instead of into pickerError: pickerError replaces
    // the candidate list with a message, which would discard the very results
    // the user just spent time identifying.
    const [applyError, setApplyError] = useState<ToastError | null>(null);
    const [matchCandidates, setMatchCandidates] = useState<AlbumMatchCandidate[]>([]);
    const [artCandidates, setArtCandidates] = useState<AlbumArtCandidate[]>([]);
    const [applyingKey, setApplyingKey] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState<string | null>(null);
    const [refreshDone, setRefreshDone] = useState(false);
    const [bump, setBump] = useState(0);
    // Editable field lists. Seeded from the album the modal opened with, and
    // replaced wholesale on save (PATCH body carries the full new list).
    const [genreList, setGenreList] = useState<string[]>(
        album.getGenres() ? album.getGenres().split(',').map((g) => g.trim()).filter(Boolean) : [],
    );
    const [artistList, setArtistList] = useState<{ id: number; name: string }[]>(
        album.getArtists().map((a) => ({ id: a.id, name: a.title })),
    ); // cache-buster after any art change
    // URL of the archive image applied during this session, so the grid can
    // ring it immediately. The server does not report which candidate URL the
    // stored file came from, so selection for archive images is only known
    // after the user picks one here; on reopen the embedded candidate (the
    // album's own stored art) carries the selected ring instead.
    const [currentArtUrl, setCurrentArtUrl] = useState<string | undefined>(undefined);
    const [artOk, setArtOk] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const artInputRef = useRef<HTMLInputElement>(null);

    // A new bump means the underlying cover file may have changed - any
    // previous "this image is broken" state no longer applies.
    useEffect(() => {
        setArtOk(true);
    }, [bump]);

    // Force-refresh: re-run the cover-art pipeline (embedded → Cover Art
    // Archive → Deezer) right now, bypassing the scan's freshness interval.
    // Needs an existing MusicBrainz identity - without one there's nothing
    // to fetch art FOR, so the button stays disabled.
    const refreshArt = async () => {
        setRefreshing(true);
        setRefreshError(null);
        setRefreshDone(false);
        try {
            const response = await fetch(`${API_BASE}/api/album?id=${album.getId()}&refresh=art`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                setRefreshError('Refresh failed');
                return;
            }
            const updated = Album.fromJson(await response.json());
            setReleaseDate(updated.getReleaseDate());
            setBump((b) => b + 1);
            setRefreshDone(true);
            onSaved(updated);
        } catch {
            setRefreshError('Refresh failed');
        } finally {
            setRefreshing(false);
        }
    };

    // Selects a tab, whose content then renders inline in the panel body
    // below (rather than stacking a separate popup on top of this modal).
    // Selecting the already-active tab collapses it back to nothing.
    const selectTab = async (tab: Tab) => {
        if (activeTab === tab) {
            setActiveTab(null);
            return;
        }
        setActiveTab(tab);
        setPickerError(null);
        setApplyingKey(null);
        setUploadError(null);

        if (tab === 'refresh') {
            await refreshArt();
            return;
        }

        setPickerLoading(true);
        try {
            // The backend route matches on "matches"/"art", not "match"/"art" -
            // translate here rather than renaming `tab` everywhere, since
            // 'match' reads better as the internal tab value.
            const candidatesParam = tab === 'match' ? 'matches' : 'art';
            const response = await fetch(`${API_BASE}/api/album?id=${album.getId()}&candidates=${candidatesParam}`, {
                credentials: 'include',
            });
            if (!response.ok) {
                setPickerError('Failed to load candidates');
                return;
            }
            const result = await response.json();
            if (tab === 'match') {
                setMatchCandidates(asArray(result));
            } else {
                setArtCandidates(asArray(result));
            }
        } catch {
            setPickerError('Failed to load candidates');
        } finally {
            setPickerLoading(false);
        }
    };

    const applyMatch = async (mbid: string) => {
        setApplyingKey(mbid);
        setApplyError(null);
        try {
            const response = await fetch(
                `${API_BASE}/api/album?id=${album.getId()}&apply_mbid=${encodeURIComponent(mbid)}`,
                { method: 'POST', credentials: 'include' }
            );
            if (!response.ok) {
                // Candidates stay on screen so the user can retry or pick a
                // different match without searching again.
                setApplyError(await toastErrorFromResponse(response, 'Failed to apply chosen match'));
                return;
            }

            // The apply endpoint wraps the album so it can also report steps
            // it skipped. Older/plain payloads (no `album` key) are still
            // accepted, so this keeps working if the endpoint is unwrapped.
            const result = await response.json();
            const albumJson = result?.album ?? result;
            const updated = Album.fromJson(albumJson);
            setReleaseDate(updated.getReleaseDate());
            setBump((b) => b + 1);
            onSaved(updated);

            // Partial apply: the album WAS updated, so report what was skipped
            // and leave the panel open rather than treating it as a failure.
            if (Array.isArray(result?.skipped_steps) && result.skipped_steps.length > 0) {
                setApplyError({
                    title: 'Applied, but some details could not be fetched',
                    skippedSteps: result.skipped_steps,
                    hint: result.warning?.hint,
                    detail: result.warning?.detail,
                });
                return;
            }

            setActiveTab(null);
        } catch (err) {
            setApplyError(toastErrorFromException(err, 'Failed to apply chosen match'));
        } finally {
            setApplyingKey(null);
        }
    };

    const applyArt = async (candidate: AlbumArtCandidate) => {
        const key = candidate.url ?? 'embedded';
        setApplyingKey(key);
        setApplyError(null);
        try {
            const params = new URLSearchParams({
                id: String(album.getId()),
                apply_art: candidate.url ?? 'embedded',
                source: candidate.source,
            });
            const response = await fetch(`${API_BASE}/api/album?${params.toString()}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                setApplyError(await toastErrorFromResponse(response, 'Failed to apply chosen cover art'));
                return;
            }
            const result = await response.json();
            const updated = Album.fromJson(result);
            setReleaseDate(updated.getReleaseDate());
            setBump((b) => b + 1);
            setCurrentArtUrl(candidate.url);
            onSaved(updated);
            setActiveTab(null);
        } catch (err) {
            setApplyError(toastErrorFromException(err, 'Failed to apply chosen cover art'));
        } finally {
            setApplyingKey(null);
        }
    };

    const uploadArt = async (file: File) => {
        setUploading(true);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const response = await fetch(`${API_BASE}/api/album/upload-art?id=${album.getId()}`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            if (!response.ok) {
                const body = await response.json().catch(() => null);
                setUploadError(body?.message || 'Failed to upload cover art');
                return;
            }
            const result = await response.json();
            const updated = Album.fromJson(result);
            setReleaseDate(updated.getReleaseDate());
            onSaved(updated);
            setBump((b) => b + 1);
        } catch {
            setUploadError('Failed to upload cover art');
        } finally {
            setUploading(false);
        }
    };

    const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) uploadArt(file);
    };

    const matchItems: PickerItem[] = matchCandidates.map((c) => ({
        key: c.mbid,
        label: c.title,
        sublabel: matchCandidateSublabel(c),
    }));

    // The embedded/already-downloaded candidate has no external URL to proxy,
    // but it is the one image the server can always render - it is the art
    // already stored for this album. Point its thumbnail at the album's own
    // cover endpoint (cache-busted with the same bump as the header preview)
    // so it shows a real preview instead of a placeholder icon, which made
    // the already-applied artwork look unavailable.
    const artItems: PickerItem[] = artCandidates.map((c) => ({
        key: c.url ?? 'embedded',
        thumbnailUrl: c.source === 'embedded' ? coverArtUrl(album.getId(), bump) : c.url,
        thumbnailIsLocal: c.source === 'embedded',
        sublabel: artCandidateLabel(c),
        // Without a URL to compare, the embedded candidate is the current art
        // whenever the album has any: it is read from the album's own files.
        selected: c.source === 'embedded' ? Boolean(album.getCoverArtPath()) : c.url === currentArtUrl,
    }));

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) {
            setError('Title cannot be empty');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/api/album?id=${album.getId()}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: title.trim(),
                    release_date: releaseDate,
                    genres: genreList,
                    artists: artistList,
                }),
            });
            const result = await response.json();
            if (!response.ok) {
                setError(result.message || 'Failed to save changes');
                return;
            }
            onSaved(Album.fromJson(result));
            onClose();
        } catch {
            setError('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div
            className="editModalOverlay"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <form className="editModalPanel" onSubmit={handleSave}>
                <button type="button" className="editModalClose" onClick={onClose} aria-label="Close">
                    <X size={16} strokeWidth={2.75} />
                </button>
                <h2 className="editModalTitle">Edit album</h2>
                <div className="editModalImageSection">
                    <div className="editModalImagePreview editModalImagePreviewLarge">
                        {artOk ? (
                            <img
                                src={coverArtUrl(album.getId(), bump)}
                                alt=""
                                onError={() => setArtOk(false)}
                            />
                        ) : (
                            <div className="editModalImagePlaceholder">
                                <ImageOff size={22} strokeWidth={2} />
                            </div>
                        )}
                        <span className="editModalImageKind">Cover art</span>
                    </div>
                </div>
                <div className="editModalFields">
                    <label className="editModalField">
                        <span className="editModalLabel">Title</span>
                        <input
                            className="editModalInput"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            maxLength={200}
                            autoFocus
                        />
                    </label>
                    <label className="editModalField">
                        <span className="editModalLabel">Release date</span>
                        <input
                            className="editModalInput"
                            type="date"
                            value={releaseDate}
                            onChange={(e) => setReleaseDate(e.target.value)}
                        />
                    </label>
                    <div className="editModalField">
                        <span className="editModalLabel">Artists</span>
                        <ArtistOrder
                            values={artistList}
                            onChange={setArtistList}
                            disabled={saving}
                        />
                    </div>
                    <div className="editModalField">
                        <span className="editModalLabel">Genres</span>
                        <GenreChips
                            values={genreList}
                            onChange={setGenreList}
                            disabled={saving}
                        />
                    </div>
                    {error && <span className="editModalError">{error}</span>}
                </div>
                <div className="editModalTabRow" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'match'}
                        className={`editModalTabButton ${activeTab === 'match' ? 'editModalTabButtonActive' : ''}`}
                        onClick={() => selectTab('match')}
                    >
                        Re-identify match…
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'art'}
                        className={`editModalTabButton ${activeTab === 'art' ? 'editModalTabButtonActive' : ''}`}
                        onClick={() => selectTab('art')}
                    >
                        Choose cover art…
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'refresh'}
                        className={`editModalTabButton ${activeTab === 'refresh' ? 'editModalTabButtonActive' : ''}`}
                        onClick={() => selectTab('refresh')}
                        disabled={!album.getMbid?.()}
                        title={album.getMbid?.() ? 'Re-fetch cover art now' : 'Identify this album first (Re-identify match…)'}
                    >
                        Refresh cover art
                    </button>
                </div>
                {activeTab && (
                    <div className="editModalTabContent">
                        {/* Rendered above the candidate list, not in place of
                            it: the results must survive a failed apply. */}
                        <ErrorToast error={applyError} onDismiss={() => setApplyError(null)} />
                        {activeTab === 'match' && (
                            <InlineCandidateList
                                variant="list"
                                items={matchItems}
                                loading={pickerLoading}
                                error={pickerError}
                                emptyMessage="No other matches found"
                                applyingKey={applyingKey}
                                onSelect={(item) => applyMatch(item.key)}
                            />
                        )}
                        {activeTab === 'art' && (
                            <>
                                <div className="editModalUploadRow">
                                    <input
                                        ref={artInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChosen}
                                    />
                                    <button
                                        type="button"
                                        className="editModalUploadButton"
                                        disabled={uploading}
                                        onClick={() => artInputRef.current?.click()}
                                    >
                                        {uploading ? 'Uploading…' : 'Upload cover art…'}
                                    </button>
                                    <span className="editModalUploadHint">JPEG, PNG or WebP, up to 5MB.</span>
                                </div>
                                {uploadError && <span className="editModalError">{uploadError}</span>}
                                <InlineCandidateList
                                    variant="grid"
                                    items={artItems}
                                    loading={pickerLoading}
                                    error={pickerError}
                                    emptyMessage="No other images found"
                                    applyingKey={applyingKey}
                                    onSelect={(item) => applyArt(artCandidates.find((c) => (c.url ?? 'embedded') === item.key)!)}
                                />
                            </>
                        )}
                        {activeTab === 'refresh' && (
                            <div className="editModalRefreshPanel">
                                {refreshing && <span className="editModalMuted">Refreshing…</span>}
                                {!refreshing && refreshError && <span className="editModalError">{refreshError}</span>}
                                {!refreshing && !refreshError && refreshDone && (
                                    <span className="editModalMuted">Cover art refreshed.</span>
                                )}
                                <button
                                    type="button"
                                    className="editModalUploadButton"
                                    disabled={refreshing}
                                    onClick={refreshArt}
                                >
                                    Refresh again
                                </button>
                            </div>
                        )}
                    </div>
                )}
                <div className="editModalActions">
                    <button type="button" className="editModalCancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="editModalSave" disabled={saving}>
                        Save
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );
}
